import React, { useMemo } from 'react';
import classNames from 'classnames';
import CustomButton from 'components/CustomButtons/Button';
import Done from '@material-ui/icons/Done';
import MoreHoriz from '@material-ui/icons/MoreHoriz';
import ChevronRight from '@material-ui/icons/ChevronRight';
import SubdirectoryArrowLeft from '@material-ui/icons/SubdirectoryArrowLeft';
import Close from '@material-ui/icons/Close';
import style from 'assets/jss/material-dashboard-pro-react/views/contactUs/trackingComplaintStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import { BodyS, BodySBold, DisplayM } from 'components/Typography/Typography';
import { Ticket } from 'utils/api/models/troubleTicketing/ticket';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import NumberInfo from 'views/ContactUs/subcomponents/QueriesAndComplaints/components/RegisteringComplaint/subcomponents/NumberInfo';

// @ts-ignore
const useStyles = makeStyles(style);

interface IShowStatusProps {
	ticketData: Ticket | null;
	ticketId: string;
	status: types.troubleTicketing.finalStatus | null;
	onClose?: () => void;
}

const ShowStatus = ({ ticketData, onClose, ticketId, status }: IShowStatusProps) => {
	const classes = useStyles();

	const Icon = useMemo(() => {
		switch (status) {
			case 'Completed':
				return <Done />;
			case 'Withdrawn':
				return <SubdirectoryArrowLeft />;
			case 'Info required from customer':
				return <div>?</div>;
			case 'Closed':
				return <Close />;
			default:
				// Accepted, Under Process
				return <MoreHoriz />;
		}
	}, [status]);

	const iconClasses = status === 'Completed' ? classes.ovalSuccess : classes.ovalWarning;

	const statusText = status?.replace(/ /g, '.').toLowerCase();

	return (
		<div className={classes.ticketWrapper}>
			<div className={classNames(classes.oval, iconClasses)}>{Icon}</div>
			<BodyS disableMargin>{translate('contact.tickettracking.success.header')}</BodyS>
			<div className={classes.ticketHeaderWrapper}>
				<DisplayM disableMargin>{ticketId}</DisplayM>
			</div>
			<BodyS disableMargin>{translate('contact.tickettracking.success.status')}</BodyS>
			<div className={classes.ticketStatus}>
				<ConditionalRender
					show={status === 'Info required from customer'}
					onTrue={() => (
						<>
							<BodyS disableMargin inline>
								{translate('contact.tickettracking.moreInformationNeeded')}
							</BodyS>
							<BodySBold disableMargin inline>
								{ticketData?.issueDate}
							</BodySBold>
						</>
					)}
					onFalse={() => (
						<>
							<BodyS disableMargin inline>
								{translate('contact.tickettracking.success.statusText1')}
							</BodyS>
							<BodySBold disableMargin inline>
								{ticketData?.issueDate}
							</BodySBold>
							<BodyS disableMargin inline>
								{translate(`contact.tickettracking.success.statusText2.${statusText}`)}
							</BodyS>
						</>
					)}
				/>
			</div>
			<ConditionalRender
				show={!!ticketData?.descriptions?.length}
				onTrue={() => (
					<div className={classes.descriptionWrapper}>
						<BodySBold disableMargin>
							{translate('history.of.complaints.complaint.details.description')}
						</BodySBold>
						<BodyS disableMargin>{ticketData?.descriptions?.[0]}</BodyS>
					</div>
				)}
			/>
			<NumberInfo
				accountNum={ticketData?.accountNumber}
				category={ticketData?.category}
				number={ticketData?.customerNumber || ''}
				getNumberDetails
			/>
			<div className={classes.moreInformationWrapper}>
				<BodyS disableMargin inline>
					<ConditionalRender
						show={status === 'Info required from customer'}
						onTrue={() => (
							<>{translate('contact.ticketResolved.you.will.be.contacted.earliest.possible')}</>
						)}
						onFalse={() => <>{translate('contact.ticketResolved.moreInformation')}</>}
					/>
				</BodyS>
				<BodySBold disableMargin inline>
					1235
				</BodySBold>
			</div>
			<ConditionalRender
				show={!!onClose}
				onTrue={() => (
					<div className={classes.trackAnotherContainer}>
						<CustomButton color="tertiary-light" className={classes.trackAnother} onClick={onClose}>
							<>
								{translate('contact.tickettracking.success.buttonLabel')} <ChevronRight />
							</>
						</CustomButton>
					</div>
				)}
			/>
		</div>
	);
};

export default ShowStatus;
